import { CodedClinicalEntityStatus } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SESSION_CLINICAL_CODES_MUTATION_SCOPE } from '../../contracts/keys';
import { type SessionClinicalCodesResponse } from '../../contracts/types';
import { confirmClinicalCode } from '../api/session-codes-api';
import {
  GET_SESSION_CLINICAL_CODES,
  invalidateSesionClinicalCodes,
} from '../query/session-clinical-codes-cache';

export const CONFIRM_EMBEDDING_STATUS = 'CONFIRM_EMBEDDING_STATUS';

export function useMutateConfirmEmbeddingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [CONFIRM_EMBEDDING_STATUS],
    scope: { id: SESSION_CLINICAL_CODES_MUTATION_SCOPE },
    mutationFn: async (input: {
      sessionId: string;
      codes: {
        code: string;
        code_system: string;
        entity_id: string;
      }[];
      invalidateWhenDone?: boolean;
    }) => {
      return await confirmClinicalCode({
        sessionId: input.sessionId,
        codes: input.codes,
      });
    },
    onSuccess: async (_, input) => {
      // use when using replace code - can make new mutation later
      if (input.invalidateWhenDone) {
        await invalidateSesionClinicalCodes(input.sessionId);
      }
    },
    onMutate: (variables) => {
      const { sessionId, codes, invalidateWhenDone } = variables;
      // update the codes in the session that are being confirmed
      if (invalidateWhenDone) {
        return;
      }
      queryClient.setQueryData<SessionClinicalCodesResponse>(
        [GET_SESSION_CLINICAL_CODES, { sessionId: sessionId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }
          const codeEntityIds = codes.map((c) => c.entity_id);
          const nextEntities = (oldData.clinical_entities ?? []).map((entity) =>
            entity.entity_id && codeEntityIds.includes(entity.entity_id)
              ? { ...entity, status: CodedClinicalEntityStatus.CONFIRMED }
              : entity
          );
          return {
            ...oldData,
            clinical_entities: nextEntities,
            updated_at: new Date().toISOString(),
          };
        }
      );
    },
  });
}
